import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { FormBuilder } from '@angular/forms';
import { Router } from '@angular/router';
import { RouteInfoService } from 'src/app/services/route-info.service';
import { BasePageListComponent } from 'src/app/shared/components/base/base.pagelist.component';
import { StockRoomBatchListDto, StockRoomBatchQuery } from '../models/stock-room-batch';
import { EasyNotificationService } from 'src/app/services/easy-notification.service';
import { VisGridComponent } from 'src/app/shared/components/grid/vis-grid/vis-grid.component';
import { ClinicDataService } from 'src/app/services/data-service/clinic-data-service';
import { ValueTextPair } from 'src/app/shared/models/value-text-pair';
import { UserCache } from 'src/app/services/user-cache';
import { ProductApi } from 'src/app/services/api-service/stocks/product-api';
import { BaseConfig } from 'src/app/services/base-config.service';
import { WebApiClient } from 'src/app/services/web-api-client.service';
import { DateHelper } from 'src/app/shared/helpers/date-helper';
import { StockQuery } from '../../product/models/product';
import { getBatchAlertDays, isBatchExpired, isBatchNearExpiry } from '../../shared/batch-expiry.util';

@Component({
  selector: 'app-stock-room-batch-list',
  templateUrl: './stock-room-batch-list.component.html',
  styleUrls: ['../../shared/stock-common.css', './stock-room-batch-list.component.css']
})
export class StockRoomBatchListComponent extends BasePageListComponent<StockRoomBatchListDto> implements OnInit {

  @ViewChild('grid') grid: VisGridComponent;
  editComponent: any = null;

  private _productId = 0;
  @Input() set productId (value: number) {
    this._productId = value;
    if (value == 0) {
        return;
    }
    if (this.queryFormGroup) {
      this.refreshList();
    }
  }
  get productId(): number {
    return this._productId;
  }
  @Input() isExpand: boolean = false;
  /** 僅列出效期警示批號 (登入警示 Dialog 用)：已過期或進入預警期間、未停用 */
  @Input() showExpiryAlertOnly: boolean = false;
  @Input() topHint: string = '';

  stockQuery: StockQuery = null;
  @Input() set externalStockQuery(value: StockQuery) {
    this.stockQuery = value;
  }

  @Output() onEmitExpandOrShrink = new EventEmitter<boolean>();
  @Output() onEmitStockQueryChange = new EventEmitter<StockQuery>();
  /** 點擊「批號明細維護」連結時通知父層關閉警示 Dialog */
  @Output() onEmitNavigate = new EventEmitter<void>();

  isStockHeadquarters = UserCache.IsStockHeadquarters();
  clinicCode = this.isStockHeadquarters ? '' : (UserCache.getLoginUser().Clinic?.Code || '');
  clinics: ValueTextPair[] = [];
  pageSize = BaseConfig.getConfig().ui.pageSize;
  today = DateHelper.today;
  todayRoc = DateHelper.getROCDateStringWithSepetator(this.today);
  systemAlertDays = 30;
  labelMsg = '';
  /** 效期警示 hint 後半句含可點擊連結，改在模板渲染 (非 innerHTML) */
  showBatchMaintainHint = false;
  // 未指定批 (虛擬溢流批) 的列 Id，供 grid 整列套紅色斜體樣式
  unassignedRowIds: string[] = [];

  constructor(
    private fb: FormBuilder,
    private webApiClient: WebApiClient,
    private notificationDI: EasyNotificationService,
    private clinicData: ClinicDataService,
    private productApi: ProductApi,
    private router: Router,
    private routeInfo: RouteInfoService
  ) {
    super();
    this.api = this.webApiClient.createHisService('Stocks/StockRoomBatch');
    this.notification = notificationDI;
  }

  async ngOnInit() {
    this.createQueryFormGroup();
    const clinicList = await this.clinicData.GetClinicList();
    this.clinics = clinicList.map(c => ({ value: c.Code, text: c.Name, extension: c.Id }));
    this.clinics = this.productApi.AddEmptyOption(this.clinics);

    if (this.stockQuery && this.queryFormGroup) {
      this.queryFormGroup.patchValue({
        clinicCode: this.stockQuery.clinicCode
      }, { emitEvent: false });
    }

    const stk001 = await this.clinicData.getParam('STK001');
    this.systemAlertDays = (stk001 && stk001.ExpirationAlertDays != null) ? stk001.ExpirationAlertDays : 30;

    if (this.showExpiryAlertOnly && !this.topHint) {
      this.topHint = `<span class="status-pending">橘字＝即將到期</span> (效期已進入預警天數內)；` +
        `<span class="text-danger">紅字＝已過期</span>。`;
      this.showBatchMaintainHint = true;
    }

    if (this.showExpiryAlertOnly) {
      this.refreshList();
    } else if (this.productId && this.queryFormGroup) {
      this.refreshList();
    }
  }

  makeQueryFormGroup() {
    return this.fb.group({
      clinicCode: [{ value: this.clinicCode, disabled: !this.isStockHeadquarters }],
      productCode: [''],
      productName: ['']
    });
  }

  makeQueryConditions(): StockRoomBatchQuery {
    const raw = this.queryFormGroup.getRawValue();
    const clinicCode = raw.clinicCode || null;
    const clinicId = clinicCode ? this.clinics.find(c => c.value === clinicCode)?.extension : null;
    const productCode = raw.productCode?.trim() || '';
    const productName = raw.productName?.trim() || '';

    return {
      clinicId: clinicId,
      clinicIds: clinicId ? '' : this.clinics.filter(c => c.extension).map(c => c.extension).join(','),
      productId: this.productId,
      productCode: productCode,
      productName: productName,
      expiryAlertOnly: this.showExpiryAlertOnly
    };
  }

  onBtnQueryClick() {
    super.onBtnQueryClick();
    if (this.stockQuery) {
      this.stockQuery.clinicCode = this.queryFormGroup.controls['clinicCode'].value;
      this.onEmitStockQueryChange.emit(this.stockQuery);
    }
  }

  expandOrShrink(isExpand: boolean) {
    this.isExpand = isExpand;
    this.onEmitExpandOrShrink.emit(this.isExpand);
  }

  /** 前往批號明細維護 */
  goProductBatch() {
    this.onEmitNavigate.emit();
    const path = this.routeInfo.makeFullUrl('/stocks/product-batch');
    this.router.navigate([path]);
  }

  handleListData(data: StockRoomBatchListDto[]) {
    const list = data ?? [];
    this.unassignedRowIds = list.filter(x => x.IsUnassigned).map(x => x.Id.toString());
    if (this.showExpiryAlertOnly) {
      this.labelMsg = list.length === 0 ? '批號效期正常' : '批號效期警示';
    } else {
      this.labelMsg = '';
    }
    return list;
  }

  private getAlertDays(dataItem: StockRoomBatchListDto): number {
    return getBatchAlertDays(dataItem.ExpirationAlertDays, this.systemAlertDays);
  }

  isExpired(dataItem: StockRoomBatchListDto): boolean {
    return isBatchExpired(dataItem.ExpirationDate, this.today);
  }

  isNearExpiry(dataItem: StockRoomBatchListDto): boolean {
    return isBatchNearExpiry(dataItem.ExpirationDate, this.getAlertDays(dataItem), this.today);
  }

  /** 效期狀態文字 (警示模式)：已過期 N 天 / 今天到期 / 剩 N 天 */
  getExpiryStatusText(dataItem: StockRoomBatchListDto): string {
    if (!dataItem.ExpirationDate) { return ''; }
    const exp = new Date(dataItem.ExpirationDate);
    exp.setHours(0, 0, 0, 0);
    const today = new Date(this.today);
    today.setHours(0, 0, 0, 0);
    const diffDays = Math.round((exp.getTime() - today.getTime()) / 86400000);
    if (diffDays < 0) { return `已過期 ${-diffDays} 天`; }
    if (diffDays === 0) { return '今天到期'; }
    return `剩 ${diffDays} 天`;
  }
}
